import { Injectable, Injector } from '@angular/core';
import { WarningInt } from '@app/interfaces/micro/passengerListStandby.interface';
import { ErrorModel } from '@models/micro/error.model';
import { Passenger } from '@app/models/micro/passenger.model';
import { Reservation } from '@app/models/micro/reservation.model';
import { NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';
import { NewRelicEventsService } from '@app/services/new-relic-events.service';
import { hasUkUptWarning, UK_UPT_PATTERN } from '@app/utils/functions/general';

// TTL defensivo: no acumular llaves indefinidamente de vuelos ya salidos. No es
// un mecanismo de expiración funcional (el bloqueo real se libera por
// releaseCheckedIn), solo evita que el Map crezca sin límite en sesiones largas.
const UK_UPT_BLOCK_TTL_MS = 24 * 60 * 60 * 1000;

export type UkUptVector = 'response_warning' | 'http_error';

@Injectable({ providedIn: 'root' })
export class UkUptGuardService {
  private readonly blocked = new Map<string, number>(); // llave -> fecha de bloqueo

  constructor(private readonly _injector: Injector) {}

  // CI-23791: delega en la funcion compartida (utils/functions/general) que
  // tambien usa ApiMapper.validateCheckin — una sola fuente del patron.
  hasUkUptWarning(warnings: WarningInt[] | null | undefined): boolean {
    return hasUkUptWarning(warnings);
  }

  isUkUptError(error: ErrorModel | null | undefined): boolean {
    if (!error) return false;
    return UK_UPT_PATTERN.test(`${error.getMessage?.() ?? ''} ${error.getRaw?.()?.reason ?? ''}`);
  }

  block(reservation: Reservation, passengers: Passenger[], vector: UkUptVector): void {
    this.prune();
    const now = Date.now();
    let blockedCount = 0;
    for (const passenger of passengers) {
      const key = this.buildKey(reservation, passenger);
      if (!key) continue;
      this.blocked.set(key, now);
      blockedCount++;
    }
    if (blockedCount > 0) {
      this.track(NEW_RELIC_EVENT.KIOSK_UK_UPT_DETECTED, reservation, {
        vector,
        passengers_count: blockedCount,
        isError: true,
        error_message: 'UK_UPT',
      });
    }
  }

  getBlocked(reservation: Reservation, passengers: Passenger[]): Passenger[] {
    this.prune();
    return passengers.filter((p) => {
      const key = this.buildKey(reservation, p);
      return !!key && this.blocked.has(key);
    });
  }

  trackRetryBlocked(reservation: Reservation, blockedCount: number): void {
    this.track(NEW_RELIC_EVENT.KIOSK_UK_UPT_RETRY_BLOCKED, reservation, {
      passengers_count: blockedCount,
      isError: true,
      error_message: 'UK_UPT',
    });
  }

  releaseCheckedIn(reservation: Reservation): void {
    if (this.blocked.size === 0) return;
    const checkedIn = (reservation?.passengers ?? []).filter((p) => p.checkinStatus);
    let releasedCount = 0;
    for (const passenger of checkedIn) {
      const key = this.buildKey(reservation, passenger);
      if (key && this.blocked.delete(key)) {
        releasedCount++;
      }
    }
    if (releasedCount > 0) {
      this.track(NEW_RELIC_EVENT.KIOSK_UK_UPT_RESOLVED, reservation, {
        passengers_count: releasedCount,
      });
    }
  }

  private buildKey(reservation: Reservation, passenger: Passenger): string {
    const pnr = reservation?.pnr?.trim().toUpperCase();
    // Corregido vs. el ticket: segmentCode vive en currentSegment, no en flightSummary.
    const segmentCode = reservation?.flightSummary?.currentSegment?.segmentCode
      ?.trim()
      .toUpperCase();
    const nameRefNumber = passenger?.nameRefNumber?.trim().toUpperCase();
    if (!pnr || !segmentCode || !nameRefNumber) return '';
    return [pnr, segmentCode, nameRefNumber].join('|');
  }

  private prune(): void {
    const cutoff = Date.now() - UK_UPT_BLOCK_TTL_MS;
    for (const [key, blockedAt] of this.blocked) {
      if (blockedAt < cutoff) this.blocked.delete(key);
    }
  }

  private track(
    event: NEW_RELIC_EVENT,
    reservation: Reservation,
    data: Record<string, string | number | boolean>
  ): void {
    try {
      const segmentCode = reservation?.flightSummary?.currentSegment?.segmentCode ?? '';
      this._injector.get(NewRelicEventsService).trackEvent(event, { segmentCode, ...data });
    } catch {
      // CI-23791: la telemetria nunca debe interrumpir el check-in.
    }
  }
}
